import { Component, OnInit, Output, EventEmitter, Input, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { SessionService } from '@app/session/session.service';
import { ConfigService, ConfigServiceOptions } from '@app/origami-api/services/config.service';
import { FieldType } from '@app/origami-api/constants';
import { GeneralFields } from '@app/services/settings/modules/general';
import { map, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ThemeColor, ThemeColorService } from '@app/services/theme-color.service';

@Component({
  standalone: false,
  selector: 'ti-about-room-viewer',
  templateUrl: './about-room-viewer.component.html',
  styleUrls: ['./about-room-viewer.component.sass'],
})
export class AboutRoomViewerComponent implements OnInit {
  strings = tiStrings.app.misc;

  @Input() isLoading = false;
  @Input() editMode = false;
  @Output() isLoadingChange = new EventEmitter<boolean>();
  @Output() editModeChange = new EventEmitter<boolean>();
  @ViewChild('templateArea') templateArea: ElementRef;

  template: string = '';
  sanitizedTemplate: SafeHtml;
  canEdit: boolean;

  constructor(
    private sessionService: SessionService,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private themeColorService: ThemeColorService,
  ) {}

  ngOnInit() {
    this.canEdit =
      this.sessionService.user.adminOrHigher ||
      (this.sessionService.user.manager &&
        this.sessionService.settings.managerAvailableModules.managerAvailableModulesConfig.aboutThisRoom.get());
    this.reloadTemplate();
  }

  reload() {
    this.reloadTemplate();
    this.setEditMode(false);
  }

  cancel() {
    this.reload();
  }

  save() {
    const settings = [
      {
        TopicName: ConfigServiceOptions.ModuleName.General,
        TopicFields: [
          {
            FieldName: GeneralFields.WelcomeScreenField.HTML,
            FieldType: FieldType.Text,
            Fieldvalue: this.template,
          },
        ],
      },
    ];

    this.setIsLoading(true);

    this.configService
      .setSettings(this.sessionService.currentRoomId, settings)
      .pipe(
        map(() => {
          this.sessionService.settings.general.welcomeScreen.html.set(this.template);
          this.reloadTemplate();
          this.setEditMode(false);
          this.setIsLoading(false);
          return true;
        }),
        catchError((e) => {
          this.reloadTemplate();
          this.setIsLoading(false);
          return throwError(e);
        }),
      )
      .subscribe();
  }

  enableEditMode() {
    this.setEditMode(true);
  }

  private setIsLoading(value: boolean) {
    this.isLoading = value;
    this.isLoadingChange.emit(value);
  }

  private setEditMode(value: boolean) {
    this.editMode = value;
    this.editModeChange.emit(value);
  }

  private reloadTemplate() {
    const userName =
      this.sessionService.user && this.sessionService.user.details ? this.sessionService.user.details.FullName : '';
    const sourceRoomName = this.sessionService.room?.info?.SourceSiteName || this.strings.none;
    this.template = this.modifyWelcomeTemplate();

    this.sanitizedTemplate = this.sanitizer.bypassSecurityTrustHtml(
      this.template.replace(/##UserName##/gi, userName).replace(/##SourceRoomName##/gi, sourceRoomName),
    );
    setTimeout(() => this.setTargetBlankAttr(Array.from(this.templateArea.nativeElement.children)));
  }

  private setTargetBlankAttr(elements: any[]): void {
    const linkTag = 'A';
    const targetAttrValue = '_blank';
    if (!elements) {
      return;
    }
    elements.forEach((child) => {
      if (child.tagName === linkTag && child.target !== targetAttrValue) {
        child.target = targetAttrValue;
      } else if (child.children) {
        this.setTargetBlankAttr(Array.from(child.children));
      } else {
        return;
      }
    });
  }

  private modifyWelcomeTemplate(): string {
    const template: string = this.sessionService.settings.general.welcomeScreen.html.get();
    const isDarkTheme: boolean = this.themeColorService.getThemeColor() === ThemeColor.Dark;
    const colorRegExp: RegExp = /color=\"[\w\W]+?\"/gim;
    const emailClass: string = 'class="template__email"';
    return isDarkTheme
      ? template.replace(/ style=\"[\w\W]+?\"/gim, '').replace(colorRegExp, emailClass)
      : template.replace(colorRegExp, emailClass);
  }
}
